/**
 * BatchPaymentModal Component
 * Modal for submitting ACH payment for selected batches
 * Tailwind version using OathUI components
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  ExclamationCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { payBatches } from '../../../../services/oathPowerReportService';
import { getPaymentAccounts } from '../../../../services/oathPaymentAccountService';
import { formatCurrencyWithSymbol, getBatchId, isLinkableBatch, roundCurrency, getBatchTypeBadge, nonNegativeAmount } from '../utils';
import { getDefaultAccount } from '../utils/paymentAccountHelpers';
import { useOathAuth } from '../../../../services/oathAuthContext';
import AccountSelect from '../components/AccountSelect';

const BatchPaymentModal = ({
  isOpen,
  onClose,
  selectedBatches = new Set(),
  paymentAmounts = {},
  batches = [],
  agencyId = null,
  onSuccess = null,
  // Called with the error message when the payment is rejected or fails, so the parent can
  // refresh a Batches to Pay list that may be stale (Bug 4430)
  onFailure = null
}) => {
  const { user } = useOathAuth();
  const currentUserId = user?.portalUserId;

  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [refreshingAccounts, setRefreshingAccounts] = useState(false);
  const [error, setError] = useState('');

  // Calculate totals from selected batches with proper currency rounding
  const totals = useMemo(() => {
    let net = 0;
    let buf = 0;
    let misc = 0;

    selectedBatches.forEach(batchId => {
      // Try different key formats that might be used
      const keyNet = `${batchId}-openNet`;
      const keyBuf = `${batchId}-openBuf`;
      const keyMisc = `${batchId}-openMisc`;

      // Defense-in-depth: parent state should already be non-negative, but make sure
      // a credit can't sneak into the ACH summary if anything upstream slips up.
      net += roundCurrency(nonNegativeAmount(paymentAmounts[keyNet]));
      buf += roundCurrency(nonNegativeAmount(paymentAmounts[keyBuf]));
      misc += roundCurrency(nonNegativeAmount(paymentAmounts[keyMisc]));
    });

    return {
      net: roundCurrency(net),
      buf: roundCurrency(buf),
      misc: roundCurrency(misc),
      total: roundCurrency(net + buf + misc)
    };
  }, [selectedBatches, paymentAmounts]);

  // Get selected batch details for display
  // Note: Use getBatchId helper because API returns reportId/powerReportId, not batchId
  const selectedBatchDetails = useMemo(() => {
    return batches.filter(b => selectedBatches.has(getBatchId(b)));
  }, [batches, selectedBatches]);

  // Load payment accounts for ACH payment
  const loadBankAccounts = useCallback(async (isBackgroundRefresh = false) => {
    if (!agencyId) {
      setError('Agency ID not found');
      return;
    }

    try {
      if (isBackgroundRefresh) {
        setRefreshingAccounts(true);
      } else {
        setLoadingAccounts(true);
      }
      setError(''); // Clear any previous errors
      console.log('[BatchPaymentModal] Loading payment accounts for agencyId:', agencyId);
      const result = await getPaymentAccounts(agencyId);
      if (result.success) {
        const activeAccounts = (result.accounts || []).filter(a => a.isActive);
        setBankAccounts(activeAccounts);

        if (isBackgroundRefresh) {
          // Preserve current selection if it still exists
          setBankAccountId(prev => {
            const stillExists = activeAccounts.some(a => String(a.accountId) === String(prev));
            return stillExists ? prev : (getDefaultAccount(activeAccounts, 'NET')?.accountId?.toString() || '');
          });
        } else {
          // Auto-select default account
          const defaultAcct = getDefaultAccount(activeAccounts, 'NET');
          setBankAccountId(defaultAcct?.accountId?.toString() || '');
        }
      } else {
        setBankAccounts([]);
        setBankAccountId('');
        setError('Failed to load payment accounts: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading payment accounts:', err);
      setBankAccounts([]);
      setBankAccountId('');
      setError('Failed to load payment accounts: ' + err.message);
    } finally {
      if (isBackgroundRefresh) {
        setRefreshingAccounts(false);
      } else {
        setLoadingAccounts(false);
      }
    }
  }, [agencyId]);

  // Load payment accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      loadBankAccounts();
    }
  }, [isOpen, loadBankAccounts]);

  // Refresh payment accounts when user returns to tab (e.g. after managing accounts in admin)
  useEffect(() => {
    if (!isOpen) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadBankAccounts(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOpen, loadBankAccounts]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
    }
  }, [isOpen]);

  // Validate form before submission
  const validateForm = () => {
    if (selectedBatches.size === 0) {
      setError('No batches selected for payment');
      return false;
    }

    // Check for any empty string amounts that should be treated as 0
    let hasEmptyAmounts = false;
    selectedBatches.forEach(batchId => {
      const net = paymentAmounts[`${batchId}-openNet`];
      const buf = paymentAmounts[`${batchId}-openBuf`];
      const misc = paymentAmounts[`${batchId}-openMisc`];
      if (net === '' || buf === '' || misc === '') {
        hasEmptyAmounts = true;
      }
    });

    if (hasEmptyAmounts) {
      setError('Please fill in all payment amounts (or enter 0 for no payment)');
      return false;
    }

    if (totals.total <= 0) {
      setError('Total payment amount must be greater than zero');
      return false;
    }

    if (!bankAccountId) {
      setError('Please select a bank account for ACH payment');
      return false;
    }

    return true;
  };

  // Handle submit button click
  const handleSubmit = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Build batch payment data with rounded, non-negative amounts. The clamp here
      // guarantees the ACH payload never carries a credit, even if upstream state drifts.
      const batchPayments = [];
      selectedBatches.forEach(batchId => {
        batchPayments.push({
          batchId: batchId,
          netAmount: roundCurrency(nonNegativeAmount(paymentAmounts[`${batchId}-openNet`])),
          bufAmount: roundCurrency(nonNegativeAmount(paymentAmounts[`${batchId}-openBuf`])),
          miscAmount: roundCurrency(nonNegativeAmount(paymentAmounts[`${batchId}-openMisc`]))
        });
      });

      const paymentData = {
        agencyId,
        batches: batchPayments,
        paymentMethod: 'ACH',
        bankAccountId: parseInt(bankAccountId),
        totalNet: totals.net,
        totalBuf: totals.buf,
        totalMisc: totals.misc
      };

      const result = await payBatches(paymentData);

      if (result.success) {
        toast.success(`Payment submitted successfully! Receipt: ${result.data?.receiptNumber || 'N/A'}`);

        if (onSuccess) {
          onSuccess(result.data);
        }

        onClose();
      } else {
        setError('Failed to submit payment: ' + (result.error || 'Unknown error'));
        toast.error('Failed to submit payment: ' + (result.error || 'Unknown error'));
        if (onFailure) onFailure(result.error || 'Unknown error');
      }
    } catch (err) {
      console.error('Error submitting batch payment:', err);
      setError('Failed to submit payment: ' + err.message);
      toast.error('Failed to submit payment: ' + err.message);
      if (onFailure) onFailure(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset all state on close
    setError('');
    setBankAccountId('');
    setBankAccounts([]);
    setLoading(false);
    setLoadingAccounts(false);
    setRefreshingAccounts(false);
    onClose();
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Submit Batch Payment"
      size="2xl"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            disabled={loading || bankAccounts.length === 0}
            icon={loading ? null : BanknotesIcon}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Submitting...
              </span>
            ) : (
              `Submit Payment (${formatCurrencyWithSymbol(totals.total)})`
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-3 sm:space-y-5">
        {/* Hero row: Selected Batches table (left, primary content) + Grand Total hero (right, supporting card).
            On lg+ they sit side-by-side; on sm/md the hero stacks above the table. */}
        <div className="grid gap-3 sm:gap-4 lg:grid-cols-5">
          {/* Selected Batches — primary content, left col-span-3.
              Internal scroll handles 20+ batches without forcing the modal to scroll. */}
          <div className="lg:col-span-3 lg:order-1 order-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 flex flex-col overflow-hidden">
            <div className="px-4 pt-3 pb-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                Selected Batches <span className="ml-1 text-gray-700 dark:text-gray-300 font-semibold">×{selectedBatches.size}</span>
              </p>
            </div>
            <div className="overflow-y-auto max-h-[280px] sm:max-h-[340px] lg:max-h-[360px]">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                      Batch #
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                      NET
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                      BUF
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                      MISC
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {selectedBatchDetails.map((batch) => {
                    const batchId = getBatchId(batch);
                    const typeBadge = getBatchTypeBadge(batch);
                    return (
                      <tr key={batchId}>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {!isLinkableBatch(batch) ? (
                              <span>{batch.reportNumber || batchId}</span>
                            ) : (
                              <a
                                href={`/app/power-inquiry?agencyIds=${batch.agencyId}&viewMode=byReport&batchId=${batchId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary dark:text-primary-light hover:underline cursor-pointer"
                              >
                                {batch.reportNumber || batchId}
                              </a>
                            )}
                            {typeBadge && (
                              <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ${typeBadge.bgColor} ${typeBadge.textColor}`}>
                                {typeBadge.label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 text-right tabular-nums">
                          {formatCurrencyWithSymbol(paymentAmounts[`${batchId}-openNet`] || 0)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 text-right tabular-nums">
                          {formatCurrencyWithSymbol(paymentAmounts[`${batchId}-openBuf`] || 0)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 text-right tabular-nums">
                          {formatCurrencyWithSymbol(paymentAmounts[`${batchId}-openMisc`] || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Grand Total — gradient hero with the headline number.
              NET / BUF / MISC are the actual addends here (they sum to Grand
              Total), so they render as top-level chips with "+" separators. */}
          <div className="lg:col-span-2 lg:order-2 order-1 relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-700 via-primary to-primary-light text-white p-4 sm:p-6 shadow-lg">
            {/* Soft circle accents */}
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 pointer-events-none" />
            <div className="absolute -bottom-16 -left-10 h-32 w-32 rounded-full bg-white/5 pointer-events-none" />

            <p className="relative text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-white/70">
              Grand Total
            </p>
            <p className="relative text-3xl sm:text-5xl font-bold tracking-tight mb-3 sm:mb-4 break-words">
              {formatCurrencyWithSymbol(totals.total)}
            </p>

            <div className="relative">
              {/* Mobile: stacked rows */}
              <div className="sm:hidden space-y-1 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Total NET</span>
                  <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(totals.net)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">+ Total BUF</span>
                  <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(totals.buf)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">+ Total MISC</span>
                  <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(totals.misc)}</span>
                </div>
              </div>

              {/* sm+: chip pills with "+" between addends */}
              <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-2">
                <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full bg-white text-sm shadow-sm">
                  <span className="text-primary-700 text-xs uppercase tracking-wider font-semibold">Total NET</span>
                  <span className="text-primary-900 font-bold tabular-nums">{formatCurrencyWithSymbol(totals.net)}</span>
                </span>
                <span className="text-white/70 font-bold text-base">+</span>
                <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full bg-white text-sm shadow-sm">
                  <span className="text-primary-700 text-xs uppercase tracking-wider font-semibold">Total BUF</span>
                  <span className="text-primary-900 font-bold tabular-nums">{formatCurrencyWithSymbol(totals.buf)}</span>
                </span>
                <span className="text-white/70 font-bold text-base">+</span>
                <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full bg-white text-sm shadow-sm">
                  <span className="text-primary-700 text-xs uppercase tracking-wider font-semibold">Total MISC</span>
                  <span className="text-primary-900 font-bold tabular-nums">{formatCurrencyWithSymbol(totals.misc)}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ACH Payment - Bank Account Selection */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <BuildingLibraryIcon className="h-5 w-5 text-primary dark:text-primary-light" />
            ACH Payment
          </h3>

          {loadingAccounts ? (
            <div className="flex items-center justify-center py-6 gap-3">
              <OathSpinner size="sm" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Loading bank accounts...
              </span>
            </div>
          ) : bankAccounts.length === 0 ? (
            <>
              <OathAlert variant="warning" className="mb-4">
                No payment accounts found. Please add a payment account before submitting payment.
              </OathAlert>
              <a
                href={`/app/admin?agencyId=${agencyId}&tab=payment-accounts`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 whitespace-nowrap px-3 py-2 text-xs bg-primary hover:bg-primary-dark text-white no-underline"
              >
                Manage Payment Accounts &rarr;
              </a>
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bank Account
                </label>
                <AccountSelect
                  value={bankAccountId}
                  onChange={setBankAccountId}
                  accounts={bankAccounts}
                  currentUserId={currentUserId}
                  ariaLabel="Bank account for batch payment"
                />
              </div>

              {/* Info Alert */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  The total amount will be debited from your selected bank account via ACH.
                </p>
              </div>

              {/* Footer line: agency context + refresh + manage link, all subtle */}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>
                  Accounts for agency <span className="font-medium text-gray-700 dark:text-gray-300">{agencyId}</span>
                  {refreshingAccounts && (
                    <span className="inline-flex items-center gap-1.5 ml-2">
                      <OathSpinner size="sm" />
                      refreshing…
                    </span>
                  )}
                </span>
                <a
                  href={`/app/admin?agencyId=${agencyId}&tab=payment-accounts`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary dark:text-primary-light hover:underline font-medium no-underline"
                >
                  Manage payment accounts &rarr;
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
      </div>
    </OathModal>
  );
};

export default BatchPaymentModal;
